import { useInfiniteQuery } from "@tanstack/react-query";
import { ApiConfig } from "@/config/api.config";
import { ApiHookProps } from '@/@types/api.types';
import { UrlHelper } from '@/utils/UrlHelper';
import { GetOrderApartmentsLookupList } from '@/features/orders/types/orders-dto';
import { useOrdersFilter } from '@/features/orders/hooks/useOrdersFilter';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';

type Props = {
    search?: string;
} & ApiHookProps;

export const useOrderApartmentsLookupApi = ({ search }: Props = {}) => {
    const axiosInstance = useAxiosAuth();
    const { filter } = useOrdersFilter();
    const { take, skip } = filter;

    const {
        data: orderApartmentsLookupList, 
        isLoading: isLoadingOrderApartmentsLookupList, 
        isError: isErrorOrderApartmentsLookupList,
        fetchNextPage: fetchOrderApartmentsLookupListNextPage,
        hasNextPage: hasOrderApartmentsLookupListNextPage,
        isFetchingNextPage: isFetchingOrderApartmentsLookupListNextPage,
    } = useInfiniteQuery({
        queryKey: [
            ApiConfig.queries.features.hotels.queryKeys.apiRefresh, 
            ApiConfig.queries.features.orders.queryKeys.orderApartmentsLookup, 
            take, skip, search
        ],
        queryFn: async ({pageParam}): Promise<GetOrderApartmentsLookupList> => {
            const url = UrlHelper.buildUrlWithQueryParams(ApiConfig.queries.features.orders.endpoints.orderApartmentsLookup, {    
                take, skip: pageParam, searchText: search?.trim()
            });
            const response = await axiosInstance.get<GetOrderApartmentsLookupList>(url);
            return response.data;
        },
        getNextPageParam: (lastPage, allPages) => {
            const totalLoaded = allPages.flatMap(page => page.items).length;
            return totalLoaded < lastPage.totalCount ? totalLoaded : undefined;
        },
        initialPageParam: skip ?? 0,
    });

    const fetchedOrderApartmentsLookupList = orderApartmentsLookupList?.pages.flatMap((page) => page.items ?? []);

    return {
        orderApartmentsLookupList: fetchedOrderApartmentsLookupList, 
        isLoadingOrderApartmentsLookupList, 
        isErrorOrderApartmentsLookupList,
        fetchOrderApartmentsLookupListNextPage,
        hasOrderApartmentsLookupListNextPage,
        isFetchingOrderApartmentsLookupListNextPage,
    }
};
